/**
 * Consultation Module - Modal Store
 *
 * Centralized modal management for the Consultation module.
 * Provides generic open/close/data actions for multiple modal types.
 */

import type { BaseStore } from "../core";
import { createStore, withLogging } from "../core";

// Extendable list of modal keys used in Consultation module
export type ConsultationModalKey =
  | "patientInfo"
  | "editPatientAllergies"
  | "addDiagnosis"
  | "addAllergy"
  | "uploadDocument"
  | "confirmLeave"
  | "investigationOrder"
  | "prescription"
  | "vitalSigns"
  | "payment"
  | "internalNotes"
  | "createTask"
  | "transferPatient"
  | "editMedication"
  | "editVaccination"
  | "templateSelector"
  | "chartModal"
  | "fileUploader"
  | "selectPackage"
  | "editDosageInstruction"
  | "medispanAlerts"
  | "deleteConfirm";

type ModalData = unknown | null;

interface ModalState {
  open: boolean;
  data: ModalData;
}

interface ConsultationModalStoreState extends BaseStore {
  // Active modal key (if any)
  activeModal: ConsultationModalKey | null;
  // Modal states keyed by modal name
  modals: Partial<Record<ConsultationModalKey, ModalState>>;

  // Actions
  openModal: (key: ConsultationModalKey, data?: ModalData) => void;
  closeModal: (key?: ConsultationModalKey) => void;
  setModalData: (key: ConsultationModalKey, data: ModalData) => void;
  reset: () => void;
}

const initialState: Pick<ConsultationModalStoreState, "activeModal" | "modals"> = {
  activeModal: null,
  modals: {},
};

export const useConsultationModalStore = createStore<ConsultationModalStoreState>(
  withLogging("ConsultationModalStore", (set) => ({
    ...initialState,
    _initialized: false,
    _lastUpdated: Date.now(),

    openModal: (key, data = null) => {
      set((state) => {
        state.activeModal = key;
        state.modals[key] = { open: true, data };
        state._lastUpdated = Date.now();
      });
    },

    closeModal: (key) => {
      set((state) => {
        const targetKey = key ?? state.activeModal;
        if (!targetKey) return;
        const current = state.modals[targetKey];
        if (current) state.modals[targetKey] = { ...current, open: false };
        if (state.activeModal === targetKey) state.activeModal = null;
        state._lastUpdated = Date.now();
      });
    },

    setModalData: (key, data) =>
      set((state) => {
        const current = state.modals[key];
        state.modals[key] = { open: current?.open ?? false, data };
        state._lastUpdated = Date.now();
      }),

    reset: () =>
      set((state) => {
        Object.assign(state, initialState);
        state._initialized = false;
        state._lastUpdated = Date.now();
      }),
  })),
  {
    // Modals are UI-ephemeral; do not persist
    name: "echo-consultation-modal-store",
    persist: false,
    version: 1,
  },
);

// Selector hooks
export const useActiveConsultationModal = () =>
  useConsultationModalStore((state) => state.activeModal);

export const useConsultationModalOpen = (key: ConsultationModalKey) => {
  const isOpen = useConsultationModalStore((state) => {
    const modalState = state.modals[key];
    return Boolean(modalState?.open);
  });
  return isOpen;
};

export const useConsultationModalData = <T = unknown>(key: ConsultationModalKey) =>
  useConsultationModalStore((state) => state.modals[key]?.data as T | null);

// Action hooks
export const useConsultationModalActions = () => {
  const openModal = useConsultationModalStore((state) => state.openModal);
  const closeModal = useConsultationModalStore((state) => state.closeModal);
  const setModalData = useConsultationModalStore((state) => state.setModalData);
  const reset = useConsultationModalStore((state) => state.reset);
  return { openModal, closeModal, setModalData, reset };
};

// 🎯 Export consultation store for certificates management
export {
  useClearCertificates,
  useConsultationActions,
  useConsultationFormData,
  useConsultationId,
  useConsultationStore,
  useGetCertificates,
  useHasUnsavedChanges,
  useIsAnyModuleLocked,
  useIsCertificatesInitialized,
  useMarkCertificatesInitialized,
  useModuleCollaborativeState,
  useSetCertificates,
  useSocketConnectionState,
  useStoreCertificates,
} from "./consultationStore";
